import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, iif, map, Observable, of, switchMap, take } from 'rxjs';
import {
    BaseTransaction,
    Transaction
} from 'src/app/features/transactions/state/transaction.model';
import { TransactionsQuery } from 'src/app/features/transactions/state/transactions.query';
import { TransactionsService } from 'src/app/features/transactions/state/transactions.service';
import { FORM_ROUTE_MAP, INITIATING_TRANSFERS } from './amend/consts';
import { setFirstHourOfDayUTC, setLastHourOfDayUTC } from '@utils';

@Injectable({
    providedIn: 'root'
})
export class AmendService {
    private _transactions: Transaction[] = [];

    get transactions() {
        return this._transactions;
    }
    set transactions(transactions: Transaction[]) {
        this._transactions = transactions;
    }
    constructor(
        private _router: Router,
        private _transactionsQuery: TransactionsQuery,
        private _transactionsService: TransactionsService
    ) {}

    amendTransaction(
        groupId: number,
        newTransaction: BaseTransaction<any>,
        isTransfer?: boolean
    ) {
        return forkJoin([
            this._transactionsService.remove(groupId),
            iif(
                () => isTransfer,
                this._transactionsService.transferInstrument(newTransaction),
                this._transactionsService.add(newTransaction, 'cash')
            )
        ]);
    }

    redirectToForm(transaction: Transaction, allTransactions?: Transaction[]) {
        // To save ourselves not having to load all transactions if not available in store before navigating,
        // we store the table data if available
        if (allTransactions) {
            this._transactionsService.upsertTransactions(allTransactions);
        }
        this._transactionsQuery
            .selectTransactionsByGroup$(transaction.transactionGroupId)
            .pipe(
                map((group) => ({
                    grp: group,
                    resolved: this._resolveRedirectPath(group)
                })),
                take(1)
            )
            .subscribe((res) => {
                if (res.resolved.transaction && res.resolved.routes.length > 0) {
                    this._router.navigate(res.resolved.routes, {
                        queryParams: {
                            groupId: res.resolved.transaction.transactionGroupId,
                            dateFrom: Date.parse(
                                res.resolved.transaction.transactionDateTime
                            ),
                            dateTo: Date.parse(res.resolved.transaction.settlementDate)
                        }
                    });
                }
            });
    }
    /**
     * From any given transaction group, get the key transactions containing the values expected to be found in its corresponding form
     * TODO: Move this to a query
     * @param transactionGroup - the transaction group to get the key transactions from
     * @returns the key transactions
     */
    resolveKeyTransactionsFromGroup(transactionGroup: Transaction[]) {
        if (transactionGroup.length === 1) {
            return { initiator: transactionGroup[0], cashflow: transactionGroup[0] };
        }
        const initiatingTransaction = transactionGroup.find((transaction) =>
            INITIATING_TRANSFERS.includes(transaction.transactionType)
        );
        const cashFlowTransaction = transactionGroup.find(
            (t) => t.instrument.isin || t.instrument.type !== 'CASH'
        );
        return { initiator: initiatingTransaction, cashflow: cashFlowTransaction };
    }

    private _resolveRedirectPath(transactionGroup: Transaction[]): {
        transaction: Transaction;
        routes: string[];
    } {
        let routes = [];
        let transaction: Transaction = undefined;
        if (transactionGroup.length > 1) {
            const { initiator } = this.resolveKeyTransactionsFromGroup(transactionGroup);
            if (!initiator) {
                return;
            }
            transaction = { ...initiator };
            routes = FORM_ROUTE_MAP[initiator.transactionType];
        } else {
            transaction = transactionGroup[0];
            let type: string = transaction.transactionType;
            if (transaction.transactionType === 'TRANSFER') {
                // Base transfer type is TRANSFER, but if it's coming from the transfer screen, we name it a SECURITY_TRANSFER
                type =
                    transaction.instrument.type !== 'CASH'
                        ? 'SECURITY_TRANSFER'
                        : 'TRANSFER';
            }
            routes = FORM_ROUTE_MAP[type];
        }
        return { transaction, routes };
    }

    verifyGroupIsAmendable(transactionGroup: Transaction[]) {
        if (transactionGroup.length > 1) {
            const { initiator } = this.resolveKeyTransactionsFromGroup(transactionGroup);
            return !!initiator;
        }
        return INITIATING_TRANSFERS.includes(transactionGroup[0].transactionType);
    }
}
